import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import PreviewIcon from '@mui/icons-material/Preview'
import {
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { ActivityHistoryModel, ActivityModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { Box, CircularProgress } from '@mui/material'
import dayjs from 'dayjs'

export default function ActivityHistoryDataGrid({
  profileId,
}: {
  profileId: string
}) {
  const [activities, setActivities] = React.useState<ActivityModel[]>([])
  const [activityHistory, setActivityHistory] = React.useState<
    ActivityHistoryModel[]
  >([])
  const [isLoading, setIsLoading] = React.useState(true)
  const navigate = useNavigate()

  const activityMap = React.useMemo(
    () => new Map(activities.map((act) => [act.id, act])),
    [activities]
  )

  React.useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) return
    setIsLoading(true)

    const fetchData = async () => {
      try {
        const historyRes = await axios.get(
          base_url + '/profile/' + profileId + '/activity',
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        setActivityHistory(historyRes.data)

        const activitiesRes = await axios.get(base_url + '/activity/', {
          headers: { Authorization: `Bearer ${token}` },
        })
        setActivities(activitiesRes.data)
      } catch (error) {
        console.error(
          "Erreur lors du chargement des données de l'historique",
          error
        )
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [profileId])

  function getActivityNameFromId(activityId: string): string {
    return activityMap.get(activityId)?.title || 'Activité inconnue'
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer cette entrée ?`)) {
      const token = localStorage.getItem('token')
      axios
        .delete(base_url + `/profile/${profileId}/activity/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          setActivityHistory(activityHistory.filter((row) => row.id !== id))
        })
        .catch((err) => {
          console.error(err)
          alert('La suppression a échoué.')
        })
    }
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate(`/profile/${profileId}/activity/${id}/edit`)
  }

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate(`/profile/${profileId}/activity/${id}`)
  }

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Titre',
      flex: 1,
      minWidth: 200,
      valueGetter: (value, row) => getActivityNameFromId(row.activityId),
    },
    {
      field: 'date',
      headerName: 'Date',
      width: 180,
      valueFormatter: (value) =>
        dayjs(Number(value) * 1000).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'giftedPoints',
      headerName: 'Points',
      type: 'number',
      width: 100,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        return [
          <GridActionsCellItem
            icon={<PreviewIcon />}
            label="Preview"
            onClick={handlePreviewClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ]
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      width: 250,
    },
  ]

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <DataGrid
      rows={activityHistory}
      columns={columns}
      slots={{ toolbar: GridToolbar }}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 10 },
        },
      }}
      pageSizeOptions={[10, 25, 50]}
      checkboxSelection
      disableRowSelectionOnClick
    />
  )
}
